import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export default function SettingsMercadoPagoPage() {
  const { profile } = useAuth();
  const [accessTokenProd, setAccessTokenProd] = useState('');
  const [accessTokenSandbox, setAccessTokenSandbox] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!profile?.company_id) return;
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (data?.settings && typeof data.settings === 'object') {
        const s = data.settings as Record<string, unknown>;
        if (typeof s.mercadopago_access_token_prod === 'string') setAccessTokenProd(s.mercadopago_access_token_prod);
        if (typeof s.mercadopago_access_token_sandbox === 'string') setAccessTokenSandbox(s.mercadopago_access_token_sandbox);
      }
      setLoaded(true);
    };
    loadSettings();
  }, [profile?.company_id]);

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setMessage(null);
    try {
      const { data: existing } = await supabase
        .from('whatsapp_configs')
        .select('id, settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      const currentSettings = (existing?.settings && typeof existing.settings === 'object') ? (existing.settings as Record<string, unknown>) : {};
      const newSettings = {
        ...currentSettings,
        mercadopago_access_token_prod: accessTokenProd,
        mercadopago_access_token_sandbox: accessTokenSandbox,
      };

      if (existing?.id) {
        await supabase.from('whatsapp_configs').update({ settings: newSettings, updated_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('whatsapp_configs').insert({ company_id: profile.company_id, settings: newSettings });
      }
      setMessage({ type: 'success', text: 'Configurações do Mercado Pago salvas com sucesso!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao salvar' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const { error } = await supabase.functions.invoke('create-mercadopago-preference', {
        body: { test: true, accessToken: accessTokenProd || accessTokenSandbox },
      });
      if (error) throw error;
      setMessage({ type: 'success', text: 'Conexão com Mercado Pago estabelecida com sucesso!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Falha na conexão com Mercado Pago' });
    } finally {
      setTesting(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const hasTokens = !!(accessTokenProd || accessTokenSandbox);

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Mercado Pago</h2>
        <p className="text-sm text-foreground-500 mt-1">Configure sua integração com o Mercado Pago para receber pagamentos</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'} text-lg`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Access Token (Produção)</label>
            <input type="password" value={accessTokenProd} onChange={(e) => setAccessTokenProd(e.target.value)} placeholder="APP_USR-..." className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Access Token (Sandbox)</label>
            <input type="password" value={accessTokenSandbox} onChange={(e) => setAccessTokenSandbox(e.target.value)} placeholder="TEST-..." className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Webhook URL</label>
            <input type="text" readOnly value={`${window.location.origin}/functions/v1/mercadopago-webhook`} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-500 outline-none cursor-text" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Status</label>
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${hasTokens ? 'bg-emerald-50 border-emerald-200' : 'bg-background-100 border-background-200'}`}>
              <span className={`w-2 h-2 rounded-full ${hasTokens ? 'bg-emerald-500' : 'bg-foreground-300'}`} />
              <span className={`text-sm font-medium ${hasTokens ? 'text-emerald-700' : 'text-foreground-500'}`}>{hasTokens ? 'Configurado' : 'Não configurado'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-background-100">
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : <><i className="ri-save-line" /> Salvar</>}
          </button>
          <button onClick={handleTest} disabled={testing || !hasTokens} className="px-5 py-2 bg-secondary-500 hover:bg-secondary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {testing ? <><i className="ri-loader-4-line animate-spin" /> Testando...</> : <><i className="ri-plug-line" /> Testar Conexão</>}
          </button>
        </div>
      </div>

      <div className="bg-background-100 rounded-2xl border border-background-200 p-5">
        <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-3">Integração Rápida</h3>
        <ol className="space-y-2 text-sm text-foreground-600 list-decimal list-inside">
          <li>Acesse <a href="https://www.mercadopago.com.br/settings/credentials" target="_blank" rel="nofollow" className="text-primary-600 hover:underline">Credenciais do Mercado Pago</a></li>
          <li>Copie o Access Token de Produção</li>
          <li>Configure a URL de Webhook acima no painel do Mercado Pago</li>
          <li>Cole o token aqui e salve</li>
        </ol>
      </div>
    </div>
  );
}